import React, { useEffect, useRef } from 'react';
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  CategoryScale,
  ChartConfiguration,
  ChartData,
  ChartOptions,
  ChartDataset,
  Legend,
  Tooltip,
  PointStyle,
} from 'chart.js';

Chart.register(LineController, LineElement, PointElement, LinearScale, Title, CategoryScale, Legend, Tooltip);

const FONT_SIZE_14 = 14;

export type GridStyle = {
  thickness?: number;
  topColors?: string[];
  bottomColors?: string[];
  topDash?: number[];
  bottomDash?: number[];
};

export type LineStyle = {
  borderColor?: string;
  backgroundColor?: string;
  thickness?: number;
  type?: string;
  point?: string;
  pointRadius?: number;
};

export type LineState = {
  hidden?: boolean;
  offsetX?: number;
  offsetY?: number;
};

export type LinesProp = {
  title?: string;
  data: (number | null)[];
  unit?: string;
  style?: LineStyle;
  state?: LineState;
  rightSide?: boolean;
};

export type CanvasProp = {
  title?: string;
  titleX?: string;
  titleYLeft?: string;
  titleYRight?: string;
  titleYLeftUnit?: string;
  titleYRightUnit?: string;
  labelsTop: string[];
  labelsBottom: string[];
  style?: string;
  height?: string;
  width?: string;
};

export type LineChartProps = {
  canvas: CanvasProp;
  lines: LinesProp[];
  grid?: GridStyle;
};

/**
 * 与えられた値に対して、最上位-1桁から切り上げた数値を返す
 * また与えられた値に対して切り上げが起きない場合も、最上位の桁に+1した数値を返す
 * 1以下なら1にする
 * @param value
 * @returns
 * @example
 * input:100 return:200
 * input:150 return:200
 * input:99.9 return 200 (小数ははじめに切り上げられる)
 */
function calcMathCeil(value: number) {
  if (value >= 1) {
    const num: number = Math.ceil(value);
    const numLength: number = String(num).length; // 3
    const val1: number = num / 10 ** (numLength - 1); // 1.5
    const val2: number = Math.ceil(val1); // 1
    if (val1 === val2) {
      return (val2 + 1) * 10 ** (numLength - 1); //
    } else {
      return val2 * 10 ** (numLength - 1);
    }
  } else {
    return 1;
  }
}

// function roundUpToNextPlace(input: number): number {
//   const num = Number(input);
//   if (num <= 0) {
//     return 0;
//   }
//   const digits = Math.pow(10, Math.floor(Math.log10(num)));
//   const rounded = Math.ceil(num / digits) * digits;
//   return rounded < 1 ? 1 : rounded < 10 ? 10 : rounded;
// }

const LineChart: React.FC<LineChartProps> = (props: LineChartProps) => {
  const dataSets: ChartDataset<'line'>[] = [];
  const lineProps: LinesProp[] = props.lines;

  let maxLeftBarValue: number = 0;
  let maxRightBarValue: number = 0;

  for (let i = 0; i < lineProps.length; i++) {
    const lineProp: LinesProp = lineProps[i];
    const lineStyle: LineStyle | undefined = lineProp.style;
    dataSets.push({
      label: lineProp.title ? `${lineProp.title}` : '',
      data: lineProp.data.map((num) => (num !== null ? num + (lineProps[i].state?.offsetY ?? 0) : null)),
      borderColor: lineStyle?.borderColor ?? '#000000',
      backgroundColor: lineStyle?.backgroundColor ?? '#000000',
      borderWidth: lineStyle?.thickness ?? 3,
      borderDash: lineProps[i].rightSide ? [] : [0],
      hidden: lineProps[i].state?.hidden ?? false,
      yAxisID: lineProp.rightSide ? 'yRight' : 'yLeft',
      pointStyle: lineStyle?.type,
      pointRadius: lineStyle?.pointRadius ?? 3,
    });
    if (lineProps[i].rightSide) {
      if (maxRightBarValue < Math.max(...lineProp.data.map((v) => v ?? 0))) {
        maxRightBarValue = Math.max(...lineProp.data.map((v) => v ?? 0));
      }
    } else {
      if (maxLeftBarValue < Math.max(...lineProp.data.map((v) => v ?? 0))) {
        maxLeftBarValue = Math.max(...lineProp.data.map((v) => v ?? 0));
      }
    }
  }
  maxRightBarValue = calcMathCeil(maxRightBarValue);
  maxLeftBarValue = calcMathCeil(maxLeftBarValue);

  const chartOptions: ChartOptions<'line'> = {
    elements: {
      point: {
        radius: 0,
        hoverRadius: 8,
      },
    },
    layout: {
      padding: {
        top: -25,
        left: 0,
        right: props.canvas.titleYRight ? 0 : 10,
        bottom: -5,
      },
    },
    maintainAspectRatio: false,
    responsive: true, // レスポンシブ対応
    plugins: {
      title: {
        display: true,
        text: props.canvas.title,
      },
      legend: {
        position: 'top',
        fullSize: false,
        labels: {
          usePointStyle: true,
        },
      },
      tooltip: {
        enabled: true,
        callbacks: {
          title: (tooltipItems) => {
            const item = tooltipItems[0];
            return `${props.canvas.labelsBottom?.[item.dataIndex] ?? ''}`;
          },
          label: (tooltipItem) => {
            return `${tooltipItem.dataset.label}:${tooltipItem.formattedValue}`;
          },
          labelPointStyle: (tooltipItem) => {
            const datasetIndex = tooltipItem.datasetIndex;
            const pointStyle = props.lines[datasetIndex]?.style?.type ?? 'circle';
            return {
              pointStyle: pointStyle as PointStyle,
              rotation: 0,
            };
          },
        },
        usePointStyle: true,
      },
    },
    scales: {
      x: {
        position: 'top',
        ticks: {
          autoSkip: false,
          callback: (index: string | number) => {
            if (typeof index === 'number') {
              return props.canvas.labelsTop[index];
            } else {
              return null;
            }
          },
          padding: -20,
          color: 'black',
          maxRotation: 0,
          minRotation: 0,
          font: {
            size: FONT_SIZE_14,
          },
        },
        title: {
          display: true,
          text: props.canvas.titleX,
        },
        grid: {
          color: props.grid?.topColors ?? 'black',
          lineWidth: props.grid?.thickness ?? 1,
        },
        border: {
          dash: props.grid?.topDash ?? [0],
        },
        beginAtZero: true,
        display: true,
      },
      x2: {
        position: 'bottom',
        ticks: {
          autoSkip: false,
          callback: (index: string | number) => {
            if (typeof index === 'number') {
              return props.canvas.labelsBottom[index];
            } else {
              return null;
            }
          },
          padding: 0,
          color: 'gray',
          maxRotation: 0,
          minRotation: 0,
          font: {
            size: FONT_SIZE_14,
          },
        },
        title: {
          display: true,
          text: props.canvas.titleX,
        },
        grid: {
          color: props.grid?.bottomColors ?? 'black',
          lineWidth: props.grid?.thickness ?? 1,
        },
        border: {
          dash: props.grid?.bottomDash ?? [0],
        },
        beginAtZero: true,
        display: true,
      },
      yLeft: {
        position: 'left',
        ticks: {
          autoSkip: false,
          padding: 0,
          color: 'gray',
          font: {
            size: FONT_SIZE_14,
          },
          callback: (value: string | number) => {
            return `${value.toLocaleString()}`;
          },
        },
        title: {
          display: true,
          text: props.canvas.titleYLeft,
        },
        beginAtZero: true,
        display: true,
        min: 0,
        max: maxLeftBarValue,
      },
      yRight: {
        position: 'right',
        ticks: {
          autoSkip: false,
          padding: 0,
          color: 'black',
          display: props.canvas.titleYRight ? true : false,
          font: {
            size: FONT_SIZE_14,
          },
        },
        title: {
          display: true,
          text: props.canvas.titleYRight,
        },
        beginAtZero: true,
        display: props.canvas.titleYRight ? true : false,
        max: 2 * maxRightBarValue,
      },
    },
  };

  const data: ChartData<'line'> = {
    labels: props.canvas.labelsTop,
    datasets: dataSets,
  };

  const chartRef = useRef<HTMLCanvasElement | null>(null);
  const chartInstance = useRef<Chart<'line'>>();

  useEffect(() => {
    if (chartRef.current) {
      const config: ChartConfiguration<'line'> = {
        type: 'line',
        data: data,
        options: chartOptions,
        plugins: [
          {
            id: 'unitDraw',
            afterDraw: (chart) => {
              const ctx = chart.ctx;
              ctx.save();
              ctx.font = `bold ${FONT_SIZE_14}px`;
              ctx.fillStyle = chart?.options?.scales?.y?.ticks?.color?.toString() || '#666';

              // 左Y軸の単位
              if (chart.scales.yLeft && props.canvas.titleYLeftUnit) {
                ctx.textAlign = 'left';
                ctx.textBaseline = 'bottom';
                ctx.fillText(`単位：[${props.canvas.titleYLeftUnit}]`, chart.scales.yLeft.left, chart.chartArea.top - FONT_SIZE_14);
              }
              ctx.restore();
            },
          },
        ],
      };
      chartInstance.current = new Chart(chartRef.current, config);
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [props, props.canvas.labelsTop, props.grid, props.lines]);

  return <canvas className={props.canvas.style} ref={chartRef} height={props.canvas.height} width={props.canvas.width} />;
};

export default LineChart;
